import { useScreenSize } from "app/hooks/shared/useScreenSize";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { useEffect, useRef } from "react";
import Slider from "react-slick";
import { REACT_APP_GERS_API_URL } from "shared/config";
import OptionConfigurationImage from "shared/ui/option-configuration-image";
import { twMerge } from "tailwind-merge";
import MainProductScene from "../product-scene";
import ArrowNext from "../standard-product-scene-carousel/components/ArrowNext";
import ArrowPrevious from "../standard-product-scene-carousel/components/ArrowPrevious";
interface Props {
  className?: string;
}
const MainProductSceneCarousel = ({ className }: Props) => {
  const {
    selectedDoor,
    layout,
    eigenwijs_layout,
    color,
    glass,
    handle,
    extra,
  } = useConfigurationOptionsContext();
  const sliderRef = useRef<Slider>(null);

  // Get the selected door name
  const doorName = selectedDoor?.name ?? "";

  // By default most of the doors have the carousel images
  let sliderImages = selectedDoor?.carrouselImageIds ?? [];

  // If its an extrawidth, XL-XXL door, change the carousel images
  if (/X+L/.test(doorName) && extra && extra.length > 0) {
    const option = extra.find((e) => e.code.includes("breedte"));
    sliderImages = option?.carrouselImageIds ?? [];
  }

  useEffect(() => {
    const goToFirstSlide = () => {
      if (sliderRef && sliderRef.current) {
        sliderRef.current.slickGoTo(0);
      }
    };

    const timer = setTimeout(goToFirstSlide, 100);

    return () => clearTimeout(timer);
  }, [layout, eigenwijs_layout, color, glass, handle]);

  const { isMobileScreen } = useScreenSize();

  const settings = {
    customPaging: function (i: number) {
      return i === 0 ? (
        // Get the basic door config layout image
        <button
          className="gg-transition-all gg-duration-300 gg-ease-in-out hover:gg-scale-105"
          aria-label={`Door ${i}`}
        >
          <img
            src={`${REACT_APP_GERS_API_URL}/OptionTemplateContainers/download?fileId=${
              selectedDoor?.imageId || ""
            }`}
            alt={`Product ${i}`}
            className="gg-aspect-square gg-h-12 gg-transform gg-cursor-pointer gg-border gg-object-cover gg-object-center gg-transition gg-duration-150 hover:gg-scale-110 sm:gg-h-20 md:gg-h-24 lg:gg-h-24 xl:gg-h-20 2xl:gg-h-28"
          />
        </button>
      ) : (
        // Render the given door images
        <button
          className="gg-transition-all gg-duration-300 gg-ease-in-out hover:gg-scale-105"
          aria-label={`Door ${i}`}
        >
          <img
            src={`${REACT_APP_GERS_API_URL}/OptionTemplateContainers/download?fileId=${
              sliderImages.slice(0, 4)[i - 1]
            }`}
            key={i}
            alt={`Product ${i}`}
            className="gg-aspect-square gg-h-12 gg-transform gg-cursor-pointer gg-border gg-object-cover gg-object-center gg-transition gg-duration-150 hover:gg-scale-110 sm:gg-h-20 md:gg-h-24 lg:gg-h-24 xl:gg-h-20 2xl:gg-h-28"
          />
        </button>
      );
    },
    // *Hide thumbnail dots on mobile screen
    // *Show on bigger screens
    dots: !isMobileScreen,
    dotsClass:
      "2xl:gg-h-28 xl:gg-h-20 lg:gg-h-24 md:gg-h-28 gg-h-15 sm:gg-h-24  sm:gg-py-4 xl:gg-py-[0.985rem] gg-justify-center gg-pt-2  gg-gap-4 xl:gg-gap-5  gg-flex slick-thumb",
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,

    // Custom arrows
    nextArrow: <ArrowNext />,
    prevArrow: <ArrowPrevious />,
  };

  // Slide show
  // Mobile hide
  // Rest sizes: render images
  const sliderSlideshow =
    sliderImages.length > 0 &&
    sliderImages.slice(0, 4).map(
      (imageId) =>
        imageId.length !== 0 && (
          <div
            className="gg-h-96 gg-w-full sm:gg-h-[33rem] md:gg-h-[39.5rem] md:gg-px-2 lg:gg-h-[30rem] xl:gg-h-[33rem] 2xl:gg-h-[39.5rem]"
            // className="gg-mt-[8.5dvh] sm:gg-mt-[6dvh] gg-w-full gg-aspect-video"
            key={imageId}
          >
            <OptionConfigurationImage
              key={imageId}
              imageId={imageId}
              className="gg-h-full gg-w-full gg-object-cover gg-object-center"
            />
          </div>
        ),
    );

  return (
    <div
      className={twMerge("gg-container gg-mx-auto gg-h-min gg-px-1", className)}
    >
      <h1 className="gg-mb-2 gg-text-lg gg-font-bold gg-text-slate-800 sm:gg-hidden">
        {doorName}
      </h1>
      <Slider ref={sliderRef} {...settings} className="">
        {/* Door configurator canvas */}
        <MainProductScene doorClassName="gg-h-96 sm:gg-h-[33rem] md:gg-h-[39.5rem] md:gg-px-2 lg:gg-h-[30rem] xl:gg-h-[33rem] 2xl:gg-h-[39.5rem] gg-col-span-full gg-w-full" />

        {/* Selected door images */}
        {sliderSlideshow}
      </Slider>
    </div>
  );
};

export default MainProductSceneCarousel;
